import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "./useAuth";
import { useUI } from "../providers/MoleUIContext";
import { DepotEnum, OperationEnum } from "../util/constants";

export type DepotAccess = {
	unrestricted: boolean;
	routes: { operation: number; source: number; destination: number }[];
	operations: { operation: number; depot: number }[];
};

const ROUTE_OPERATIONS: number[] = [OperationEnum.ENTRY, OperationEnum.TRANSFER, OperationEnum.DISCARD];
const DEPOT_OPERATIONS: number[] = [OperationEnum.INVENTORY, OperationEnum.CONVERSION, OperationEnum.PRODUCTION];

/**
 * Mirrors backend user_route_mapping / user_operation_mapping checks so a
 * posting is refused before it is sent. Until access has loaded, checks pass
 * and the backend stays the authority.
 */
export const useDepotAccess = () => {
	const { api, accessVersion } = useAuth();
	const { queueReRun, t } = useUI();
	const [access, setAccess] = useState<DepotAccess | null>(null);
	// api / queueReRun are not memoized upstream; refs keep the fetch from
	// re-running on every render.
	const apiRef = useRef(api);
	const queueReRunRef = useRef(queueReRun);
	apiRef.current = api;
	queueReRunRef.current = queueReRun;

	useEffect(() => {
		let cancelled = false;
		const fetchAccess = async () => {
			const res = await apiRef.current.get("/users/me/depot-access");
			if (cancelled) return;
			if (res?.status === 200) {
				setAccess(res.data);
			} else if (res?.status === 401 || res?.status === 422) {
				queueReRunRef.current(fetchAccess);
			}
		};
		fetchAccess();
		return () => {
			cancelled = true;
		};
	}, [accessVersion]);

	const canUseRoute = useCallback((operation: number, source: number, destination: number): boolean => {
		if (!access || access.unrestricted) return true;
		if (!ROUTE_OPERATIONS.includes(Number(operation))) return true;
		// Sales routes are governed by user POS mapping.
		if (Number(source) === DepotEnum.SALES || Number(destination) === DepotEnum.SALES) return true;
		return access.routes.some(r =>
			r.operation === Number(operation) && r.source === Number(source) && r.destination === Number(destination)
		);
	}, [access]);

	const canUseDepotOperation = useCallback((operation: number, depot: number): boolean => {
		if (!access || access.unrestricted) return true;
		if (!DEPOT_OPERATIONS.includes(Number(operation))) return true;
		return access.operations.some(o => o.operation === Number(operation) && o.depot === Number(depot));
	}, [access]);

	const ensureRoute = useCallback((operation: number, source: number, destination: number): boolean => {
		const allowed = canUseRoute(operation, source, destination);
		if (!allowed) toast.error(t("common:toast.error.routeNotAssigned"), { autoClose: 3000 });
		return allowed;
	}, [canUseRoute, t]);

	const ensureDepotOperation = useCallback((operation: number, depot: number): boolean => {
		const allowed = canUseDepotOperation(operation, depot);
		if (!allowed) toast.error(t("common:toast.error.depotOperationNotAssigned"), { autoClose: 3000 });
		return allowed;
	}, [canUseDepotOperation, t]);

	return { access, canUseRoute, canUseDepotOperation, ensureRoute, ensureDepotOperation };
};
